import { TRY, fmtDate, fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { confirm } from "@/components/ConfirmDialog";
import { Banknote, CreditCard, Landmark, ScrollText, ArrowLeftRight, Pencil, Trash2, Plus, Wallet, Lock, Info } from "lucide-react";
import { cn } from "@/lib/utils";
import { METHOD_LABEL } from "./ledgerUtils";

const METHOD = {
  nakit: { icon: Banknote, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  kredi_karti: { icon: CreditCard, cls: "bg-sky-50 text-sky-700 border-sky-200" },
  havale: { icon: Landmark, cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  cek: { icon: ScrollText, cls: "bg-amber-50 text-amber-800 border-amber-200" },
};
const SOURCE = { quick_sale: "Hızlı satış", quick_sale_collect: "Hızlı satış tahsilatı", quote: "Teklif", cari_defter: "Cari defter", excel_import: "Excel aktarımı", cheque: "Çek portföyü", folder_transfer: "Klasör aktarımı", prep: "Hazırlık listesi", agenda: "Ajanda" };
const isInv = (p) => p.source === "invoice";
const isOut = (p) => p.direction === "giden";
const sum = (arr) => arr.reduce((s, p) => s + Number(p.amount || 0), 0);

function PaymentCard({ p, isAdmin, onEdit, onDelete }) {
  const m = METHOD[p.method] || { icon: ArrowLeftRight, cls: "bg-slate-50 text-slate-700 border-slate-200" };
  const MI = m.icon;
  const inv = isInv(p), out = isOut(p);
  const tags = [];
  if (p.invoice_no) tags.push(`Fatura #${p.invoice_no}`);
  if (p.method === "cek" && p.due_date) tags.push(`Vade ${fmtDate(p.due_date)}`);
  if (p.method === "cek") tags.push(p.cleared ? "Tahsil edildi" : "Tahsil bekliyor");
  if (p.from_whom) tags.push(`Kimden: ${p.from_whom}`);
  if (p.serial_no) tags.push(`Seri ${p.serial_no}`);
  if (p.document_no) tags.push(`Evrak ${p.document_no}`);
  if (SOURCE[p.source]) tags.push(`Kaynak: ${SOURCE[p.source]}`);
  const desc = [p.note, ...tags].filter(Boolean).join(" · ");
  const edited = p.updated_by && p.updated_at;
  return (
    <tr className="h-9 hover:bg-slate-50/80 transition-colors" data-testid={`ledger-pay-${p.id}`}>
      <td className="pl-4 pr-2 whitespace-nowrap tabular-nums font-semibold text-slate-700">{fmtDate(p.date)}</td>
      <td className="px-2 whitespace-nowrap"><span className={cn("inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10.5px] font-bold", m.cls)} data-testid={`ledger-pay-method-${p.id}`}><MI className="h-3 w-3" />{METHOD_LABEL[p.method] || p.method || "Diğer"}</span></td>
      <td className={cn("px-2 text-right whitespace-nowrap tabular-nums font-head font-black", out ? "text-red-600" : "text-[#16A34A]")} data-testid={`ledger-pay-amount-${p.id}`}>{out ? "− " : ""}{TRY(p.amount)}</td>
      <td className="px-2 max-w-[340px]">
        <div className="flex items-center gap-1 min-w-0">
          {inv && <span className="shrink-0 rounded border border-[#0F2745]/20 bg-[#0F2745]/5 text-[#0F2745] text-[10px] font-bold px-1.5" data-testid={`ledger-pay-inv-badge-${p.id}`}>Fatura ödemesi</span>}
          {out && <span className="shrink-0 rounded border border-red-200 bg-red-50 text-red-700 text-[10px] font-bold px-1.5">Yapılan ödeme</span>}
          <span className="truncate text-slate-600" title={desc} data-testid={`ledger-pay-note-${p.id}`}>{desc || "—"}</span>
        </div>
      </td>
      <td className="px-2 whitespace-nowrap text-[11px] text-slate-500">
        <span data-testid={`ledger-pay-created-${p.id}`}>{p.created_at ? fmtDateTime(p.created_at) : "—"}</span>
        <span className="mx-1 text-slate-300">·</span>
        <span data-testid={`ledger-pay-by-${p.id}`}>{p.created_by || "—"}</span>
        {edited && <span className="ml-1.5 inline-flex items-center gap-0.5 text-slate-400" title={`Düzenleme: ${fmtDateTime(p.updated_at)} · ${p.updated_by}`} data-testid={`ledger-pay-updated-${p.id}`}><Pencil className="h-3 w-3" />düzenlendi</span>}
      </td>
      <td className="pl-2 pr-3 whitespace-nowrap text-right">
        {isAdmin && !inv && (
          <span className="inline-flex gap-1">
            <button type="button" onClick={() => onEdit(p)} className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 hover:text-[#0F2745] hover:border-[#0F2745]/40" title="Ödemeyi düzenle" data-testid={`ledger-pay-edit-${p.id}`}><Pencil className="h-3.5 w-3.5" /></button>
            <button type="button" onClick={() => onDelete(p)} className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 hover:text-destructive hover:border-red-300" title="Ödemeyi sil" data-testid={`ledger-pay-delete-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
          </span>
        )}
        {inv && <span className="inline-flex h-7 w-7 items-center justify-center text-slate-300" title="Fatura ödemesi — Faturalar sekmesinden yönetilir" data-testid={`ledger-pay-lock-${p.id}`}><Lock className="h-3.5 w-3.5" /></span>}
      </td>
    </tr>
  );
}

// Cari defter ödeme paneli — TÜM ödemeler (defter + fatura + yapılan), kayıt saati, ekleyen/düzenleyen, düzenle & sil
export function LedgerPayments({ payments, ledgerPaid, isAdmin, onAdd, onEdit, onDelete }) {
  const list = [...payments].sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")) || String(b.created_at || "").localeCompare(String(a.created_at || "")));
  const incoming = payments.filter((p) => !isOut(p));
  const invPaid = sum(incoming.filter(isInv));
  const outPaid = sum(payments.filter(isOut));
  const askDelete = async (p) => {
    const ok = await confirm({ title: "Ödeme silinsin mi?", description: `${fmtDate(p.date)} tarihli ${TRY(p.amount)} tutarındaki ${METHOD_LABEL[p.method] || ""} ödeme silinecek; cari bakiye buna göre güncellenir.`, confirmText: "Evet, Sil", destructive: true });
    if (ok) onDelete(p);
  };
  const chips = [["Defter ödemeleri", ledgerPaid, "ledger-pay-sum-ledger"], invPaid > 0 && ["Fatura ödemeleri", invPaid, "ledger-pay-sum-invoice"], outPaid > 0 && ["Yapılan ödemeler", outPaid, "ledger-pay-sum-out"]].filter(Boolean);
  return (
    <section className="rounded-xl border bg-card shadow-sm overflow-hidden flex flex-col" data-testid="ledger-payments">
      <header className="relative bg-[#0F2745] text-white px-4 py-2 flex flex-wrap items-center gap-3">
        <span className="absolute left-0 right-0 bottom-0 h-[2px] bg-[#F2B705]" />
        <span className="h-8 w-8 rounded-lg bg-white/10 text-[#F2B705] flex items-center justify-center"><Wallet className="h-4 w-4" /></span>
        <div className="min-w-0">
          <h3 className="font-head text-xs font-black uppercase tracking-[0.18em]">Tüm Ödemeler</h3>
          <p className="text-[11px] text-slate-300" data-testid="ledger-pay-count">{payments.length} kayıt{list[0] ? ` · son ödeme ${fmtDate(list[0].date)}` : ""}</p>
        </div>
        <div className="ml-auto text-right">
          <div className="text-[10px] uppercase tracking-wider text-slate-400">Toplam Tahsilat</div>
          <div className="font-head font-black text-lg tabular-nums text-[#4ADE80]" data-testid="ledger-pay-total">{TRY(sum(incoming))}</div>
        </div>
        <Button size="sm" className="h-8 bg-[#F2B705] text-[#0F2745] hover:bg-[#ffc933] font-bold" onClick={onAdd} data-testid="ledger-add-payment"><Plus className="h-4 w-4 mr-1" />Ödeme Ekle</Button>
      </header>
      {payments.length > 0 && (
        <div className="px-4 py-2 border-b bg-slate-50 space-y-1" data-testid="ledger-pay-breakdown">
          <div className="flex flex-wrap gap-1.5">
            {chips.map(([label, v, testid]) => <span key={testid} className="inline-flex items-center gap-1.5 rounded-full border bg-white px-2.5 py-0.5 text-[11px] font-semibold text-slate-700" data-testid={testid}>{label}<b className="tabular-nums text-[#0F2745]">{TRY(v)}</b></span>)}
          </div>
          {invPaid > 0 && <p className="text-[11px] text-muted-foreground inline-flex items-start gap-1"><Info className="h-3 w-3 mt-0.5 shrink-0" />Defter bakiyesinden yalnız defter ödemeleri düşer; fatura ödemeleri ilgili faturayı kapatır.</p>}
        </div>
      )}
      <div className="overflow-x-auto">
        {list.length === 0 ? (
          <div className="px-4 py-10 text-center" data-testid="ledger-pay-empty">
            <Wallet className="h-7 w-7 mx-auto text-slate-300" />
            <p className="mt-2 text-sm font-semibold text-slate-600">Henüz ödeme yok</p>
            <p className="text-xs text-muted-foreground">Tahsilat eklendiğinde tarih, saat, yöntem ve ekleyen kişiyle burada listelenir.</p>
          </div>
        ) : (
          <table className="w-full text-xs" data-testid="ledger-pay-list">
            <thead className="bg-slate-50 border-b text-[9.5px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="pl-4 pr-2 py-1.5 text-left font-bold">Tarih</th>
                <th className="px-2 py-1.5 text-left font-bold">Yöntem</th>
                <th className="px-2 py-1.5 text-right font-bold">Tutar</th>
                <th className="px-2 py-1.5 text-left font-bold">Açıklama</th>
                <th className="px-2 py-1.5 text-left font-bold">Kayıt · Ekleyen</th>
                <th className="pl-2 pr-3 py-1.5" />
              </tr>
            </thead>
            <tbody className="divide-y">
              {list.map((p) => <PaymentCard key={p.id} p={p} isAdmin={isAdmin} onEdit={onEdit} onDelete={askDelete} />)}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
